import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from '@/lib/api';
import type {
    SavedView,
    CreateSavedViewInput,
    UpdateSavedViewInput,
    ViewType,
    ColumnDef,
    CreateColumnBody,
    UpdateColumnBody,
    ReorderColumnsBody,
} from '@/lib/schemas';

// ============================================
// QUERY KEYS
// ============================================

export const viewKeys = {
    all: ['views'] as const,
    lists: () => [...viewKeys.all, 'list'] as const,
    // Per-project list variants. list(projectId, type) covers the type-filtered
    // queries (KANBAN / TABLE / ALL). To match ALL type variants for a single
    // project we expose `listAll(projectId)` below.
    list: (projectId: string, type?: ViewType) =>
        [...viewKeys.lists(), projectId, type ?? 'ALL'] as const,
    listAll: (projectId: string) => [...viewKeys.lists(), projectId] as const,
    detail: (id: string) => [...viewKeys.all, 'detail', id] as const,
};

// ============================================
// TYPES
// ============================================

export interface ViewsResponse {
    views: SavedView[];
    total: number;
}

export interface ToggleFavoriteResponse {
    message: string;
    isFavorite: boolean;
    favoritePosition?: number;
}

export interface ReorderFavoritesResponse {
    favorites: Array<{ viewId: string; position: number }>;
}

// Response from the column-CRUD endpoints. The backend also flags
// `viewIdChanged` when a non-owner editing a shared/default view triggers
// the auto-clone from t1; the frontend uses that hint to refresh its
// local active-view reference (F2 from the t1 review).
export interface ColumnMutationResponse {
    view: SavedView;
    column?: ColumnDef;
    viewIdChanged: boolean;
}

// ============================================
// HOOKS
// ============================================

// Fetch the saved views for a project. When `type` is supplied the backend
// restricts the response to that view type and (for KANBAN) transparently
// seeds a default status-grouped board on first open, so the caller doesn't
// need a separate "ensure default" call.
export function useSavedViews(projectId: string, type?: ViewType) {
    return useQuery({
        queryKey: viewKeys.list(projectId, type),
        queryFn: async () => {
            const queryString = type ? `?type=${encodeURIComponent(type)}` : '';
            const data = await apiGet<ViewsResponse>(`/projects/${projectId}/views${queryString}`);
            return data;
        },
        enabled: !!projectId,
        staleTime: 60 * 1000, // 1 minute
    });
}

// ============================================
// CACHE INVALIDATION HELPERS
// ============================================
//
// Single source of truth for "I just changed a view or column — please
// refetch". Every Kanban mutation calls one of these so the React Query
// cache stays coherent without each hook re-implementing the pattern.
// Without this the user's repro ("create a column → it doesn't show up
// until I refresh") still happens — onSuccess invalidation is the fix.

// Invalidate every view list cache for this project (KANBAN + TABLE +
// unfiltered variants), plus the detail cache for the new (possibly
// forked) view id. Used by create / update / delete column mutations and
// the legacy view CRUD hooks.
function invalidateAllViewsForProject(
    queryClient: QueryClient,
    projectId: string,
    options?: { detailViewId?: string; removeDetailViewId?: string }
) {
    // Match ALL type variants for this project (prefix match). React Query
    // invalidates every entry whose key starts with the supplied prefix.
    queryClient.invalidateQueries({ queryKey: viewKeys.listAll(projectId) });

    if (options?.detailViewId) {
        queryClient.invalidateQueries({ queryKey: viewKeys.detail(options.detailViewId) });
    }
    if (options?.removeDetailViewId) {
        // Used when the original view id no longer exists (auto-clone fork).
        queryClient.removeQueries({ queryKey: viewKeys.detail(options.removeDetailViewId) });
    }
}

// Local helper: every column mutation may auto-clone the view (when the
// requester isn't the owner of a shared/default view — see t1 fix). When
// that happens the response's viewId no longer matches the active view
// in our local state. Each mutation hook passes a callback so the caller
// can re-key its local `activeView` without manually re-querying.
type ViewIdSwapHandler = (newViewId: string) => void;

function withCachePatch<TData extends ColumnMutationResponse>(
    queryClient: ReturnType<typeof useQueryClient>,
    projectId: string,
    prevViewId: string,
    data: TData,
    onViewIdSwap?: ViewIdSwapHandler
): TData {
    // Patch every relevant list-cache entry in place. We can't just patch
    // the type-filtered list because the caller may have other mounts
    // holding the unfiltered list — keeping them in sync avoids a flash
    // of stale data on the next render.
    const patchList = (key: readonly unknown[]) => {
        queryClient.setQueryData<ViewsResponse>(key, (old) => {
            if (!old) return old;
            return {
                ...old,
                views: old.views.map((v) => (v.id === prevViewId ? data.view : v.id === data.view.id ? data.view : v)),
            };
        });
    };
    patchList(viewKeys.list(projectId, 'KANBAN'));
    patchList(viewKeys.list(projectId, 'TABLE'));
    patchList(viewKeys.list(projectId));

    if (data.viewIdChanged && onViewIdSwap) {
        onViewIdSwap(data.view.id);
    }
    return data;
}

// ============================================
// COLUMN-CRUD HOOKS
// ============================================

export function useCreateColumn(
    projectId: string,
    viewId: string,
    onViewIdSwap?: ViewIdSwapHandler
) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (body: CreateColumnBody) => {
            const data = await apiPost<ColumnMutationResponse>(
                `/projects/${projectId}/views/${viewId}/columns`,
                body
            );
            return withCachePatch(queryClient, projectId, viewId, data, onViewIdSwap);
        },
        // t12 fix: drop the post-success invalidation. withCachePatch
        // above already wrote the server response into every relevant
        // ['views','list', projectId, type] cache entry via setQueryData
        // — invalidating afterwards triggered a refetch that briefly
        // raced the in-flight patch and produced a "snap-back then
        // forward" flicker on column reorder (#1). Cross-tab sync
        // still works because the socket handler in use-realtime.ts
        // invalidates the cache when OTHER users mutate.
        // onViewIdSwap still fires from inside withCachePatch so the
        // auto-clone fork re-keys the parent's activeViewId.
    });
}

export function useUpdateColumn(
    projectId: string,
    viewId: string,
    onViewIdSwap?: ViewIdSwapHandler
) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ colId, body }: { colId: string; body: UpdateColumnBody }) => {
            const data = await apiPatch<ColumnMutationResponse>(
                `/projects/${projectId}/views/${viewId}/columns/${encodeURIComponent(colId)}`,
                body
            );
            return withCachePatch(queryClient, projectId, viewId, data, onViewIdSwap);
        },
        // t12 fix: same — see useCreateColumn above. No invalidate;
        // withCachePatch writes the authoritative server response.
    });
}

export function useDeleteColumn(
    projectId: string,
    viewId: string,
    onViewIdSwap?: ViewIdSwapHandler
) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (colId: string) => {
            // apiDelete returns unknown, so we manually decode the body
            // and replay the cache-patch step.
            const res = await fetch(
                `/api/projects/${projectId}/views/${viewId}/columns/${encodeURIComponent(colId)}`,
                { method: 'DELETE', credentials: 'include' }
            );
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `HTTP ${res.status}`);
            }
            const data = (await res.json()) as ColumnMutationResponse;
            return withCachePatch(queryClient, projectId, viewId, data, onViewIdSwap);
        },
        // t12 fix: same — see useCreateColumn above. No invalidate.
    });
}

export function useReorderColumns(
    projectId: string,
    viewId: string,
    onViewIdSwap?: ViewIdSwapHandler
) {
    const queryClient = useQueryClient();
    // Snapshot all type variants for this project so we can rollback
    // consistently on error. Each entry is keyed by its serialised
    // queryKey so we can write back to the exact same slot.
    function snapshotLists() {
        const allKeys = queryClient
            .getQueryCache()
            .findAll({ queryKey: viewKeys.lists() })
            .map((q) => q.queryKey)
            .filter((k) => Array.isArray(k) && k[2] === projectId);
        const map = new Map<string, ViewsResponse | undefined>();
        for (const key of allKeys) {
            map.set(JSON.stringify(key), queryClient.getQueryData<ViewsResponse>(key));
        }
        return map;
    }
    return useMutation({
        mutationFn: async (body: ReorderColumnsBody) => {
            const data = await apiPost<ColumnMutationResponse>(
                `/projects/${projectId}/views/${viewId}/columns/reorder`,
                body
            );
            return withCachePatch(queryClient, projectId, viewId, data, onViewIdSwap);
        },
        // t15: optimistic update — patch the cache with the new column
        // order BEFORE the API call resolves. Without this the
        // visibleColumns array stays at the OLD order for the duration
        // of the await (typically 50-200ms), which is the "column
        // teleport to OLD then NEW" flicker the user reported. Combined
        // with the inline `transition: 'none'` fix in kanban-column.tsx
        // this gives a true single-motion drop.
        onMutate: async (body: ReorderColumnsBody) => {
            await queryClient.cancelQueries({ queryKey: viewKeys.lists() });
            const previous = snapshotLists();
            const patchList = (key: readonly unknown[]) => {
                queryClient.setQueryData<ViewsResponse>(key, (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        views: old.views.map((v) => {
                            if (v.id !== viewId) return v;
                            const config = (v.config || {}) as Record<string, unknown>;
                            const oldCols = Array.isArray(config.kanbanColumns)
                                ? (config.kanbanColumns as unknown[])
                                : [];
                            // R5 fix: build the id→col lookup from ALL
                            // existing columns, not just the
                            // non-reordered extras. The previous code
                            // populated byId from `preserved` (the
                            // columns NOT in body.columnIds), then
                            // looked up each body.columnIds entry in
                            // byId — but the body entries are the
                            // columns to REORDER, not the extras, so
                            // every lookup returned undefined and the
                            // optimistic patch set kanbanColumns to
                            // []. The user saw columns disappear
                            // (because allColumns resolved to []) and
                            // then withCachePatch restored the
                            // server's correct order, but the flicker
                            // was unmistakable. Now we look up the
                            // body entries in the full byId map and
                            // append any columns the client omitted
                            // (defensive — should not happen for a
                            // reorder that touches every visible
                            // column).
                            const byId = new Map<string, Record<string, unknown>>();
                            for (const c of oldCols) {
                                if (!c || typeof c !== 'object') continue;
                                const id = (c as { id?: unknown }).id;
                                if (typeof id !== 'string') continue;
                                byId.set(id, c as Record<string, unknown>);
                            }
                            const nextCols: Record<string, unknown>[] = [];
                            for (const id of body.columnIds) {
                                const col = byId.get(id);
                                if (col) nextCols.push(col);
                            }
                            for (const c of oldCols) {
                                if (!c || typeof c !== 'object') continue;
                                const id = (c as { id?: unknown }).id;
                                if (typeof id !== 'string') continue;
                                if (!body.columnIds.includes(id)) {
                                    nextCols.push(c as Record<string, unknown>);
                                }
                            }
                            // Cast through unknown — TypeScript can't
                            // statically verify the same shape we got
                            // out of getQueryData goes back in. The
                            // mutationFn / withCachePatch in t12 then
                            // overwrites with the authoritative server
                            // response, so any transient mismatch is
                            // short-lived.
                            return {
                                ...v,
                                config: { ...config, kanbanColumns: nextCols },
                            } as SavedView;
                        }),
                    };
                });
            };
            patchList(viewKeys.list(projectId, 'KANBAN'));
            patchList(viewKeys.list(projectId, 'TABLE'));
            patchList(viewKeys.list(projectId));
            return { previous };
        },
        onError: (_err, _body, context) => {
            if (context?.previous) {
                for (const [json, data] of context.previous) {
                    queryClient.setQueryData(JSON.parse(json), data);
                }
            }
        },
        // t12 fix retained: no invalidate. The optimistic onMutate
        // writes the new order immediately; mutationFn / withCachePatch
        // then writes the server-confirmed order. We DON'T skip the
        // mutationFn patch — the server order is authoritative.
    });
}

// ============================================
// LEGACY VIEW-CRUD HOOKS
// ============================================
// These were the original hooks from t2 and were used throughout the
// saved-views-panel and project-detail page. t4 added the column CRUD
// hooks above; the original view hooks remain so callers don't break.
// t8 rewired every onSuccess to invalidate ALL view-list variants for
// the project so the sidebar / KanbanView refresh without a manual
// reload.

export function useCreateSavedView(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateSavedViewInput) => {
            return apiPost<SavedView>(`/projects/${projectId}/views`, data);
        },
        onSuccess: (created) => {
            // R24: insert the new view into the type-filtered list cache
            // immediately. Document mode selects the created view right
            // away; without this the list still looked empty for one
            // refetch, briefly re-showing the "create your first
            // document" CTA (flash + a double-click could create two
            // documents).
            queryClient.setQueryData<ViewsResponse | undefined>(
                viewKeys.list(projectId, created.type),
                (old) => {
                    if (!old?.views) return old;
                    if (old.views.some((v) => v.id === created.id)) return old;
                    return { views: [...old.views, created], total: (old.total ?? old.views.length) + 1 };
                }
            );
            invalidateAllViewsForProject(queryClient, projectId, {
                detailViewId: created.id,
            });
        },
    });
}

export function useUpdateSavedView(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ viewId, data }: { viewId: string; data: UpdateSavedViewInput }) => {
            return apiPatch<SavedView>(`/views/${viewId}`, data);
        },
        onSuccess: (_updated, { viewId }) => {
            invalidateAllViewsForProject(queryClient, projectId, {
                detailViewId: viewId,
            });
        },
    });
}

export function useDeleteSavedView(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (viewId: string) => {
            return apiDelete<{ message: string }>(`/views/${viewId}`);
        },
        onSuccess: (_data, viewId) => {
            invalidateAllViewsForProject(queryClient, projectId);
            // The detail row is gone too — drop it from cache so a stale
            // getQueryData('view', id) call doesn't return ghost data.
            queryClient.removeQueries({ queryKey: viewKeys.detail(viewId) });
        },
    });
}

export function useDuplicateSavedView(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (viewId: string) => {
            return apiPost<SavedView>(`/views/${viewId}/duplicate`);
        },
        onSuccess: (created) => {
            invalidateAllViewsForProject(queryClient, projectId, {
                detailViewId: created.id,
            });
        },
    });
}

export function useReorderViews(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (viewIds: string[]) => {
            return apiPost<ViewsResponse>(`/projects/${projectId}/views/reorder`, { viewIds });
        },
        onMutate: async (viewIds) => {
            // Snapshot all type variants so we can rollback consistently.
            const allKeys = queryClient
                .getQueryCache()
                .findAll({ queryKey: viewKeys.lists() })
                .map((q) => q.queryKey)
                .filter((k) => Array.isArray(k) && k[2] === projectId);

            const previousByKey = new Map<string, ViewsResponse | undefined>();
            for (const key of allKeys) {
                previousByKey.set(JSON.stringify(key), queryClient.getQueryData<ViewsResponse>(key));
            }

            for (const key of allKeys) {
                queryClient.setQueryData<ViewsResponse>(key, (old) => {
                    if (!old) return old;
                    const reorderedViews = viewIds
                        .map((id, index) => {
                            const view = old.views.find((v) => v.id === id);
                            return view ? { ...view, position: index } : null;
                        })
                        .filter(Boolean) as SavedView[];
                    return { ...old, views: reorderedViews };
                });
            }

            return { previousByKey };
        },
        onError: (_err, _viewIds, context) => {
            if (context?.previousByKey) {
                for (const [json, data] of context.previousByKey) {
                    queryClient.setQueryData(JSON.parse(json), data);
                }
            }
        },
        onSettled: () => {
            invalidateAllViewsForProject(queryClient, projectId);
        },
    });
}

export function useToggleFavoriteView(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (viewId: string) => {
            return apiPost<ToggleFavoriteResponse>(`/projects/${projectId}/views/favorite`, { viewId });
        },
        onMutate: async (viewId) => {
            const allKeys = queryClient
                .getQueryCache()
                .findAll({ queryKey: viewKeys.lists() })
                .map((q) => q.queryKey)
                .filter((k) => Array.isArray(k) && k[2] === projectId);

            const previousByKey = new Map<string, ViewsResponse | undefined>();
            for (const key of allKeys) {
                previousByKey.set(JSON.stringify(key), queryClient.getQueryData<ViewsResponse>(key));
            }

            for (const key of allKeys) {
                queryClient.setQueryData<ViewsResponse>(key, (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        views: old.views.map((view) => {
                            if (view.id === viewId) {
                                return {
                                    ...view,
                                    isFavorite: !view.isFavorite,
                                    favoritePosition: view.isFavorite ? null : 999,
                                };
                            }
                            return view;
                        }),
                    };
                });
            }

            return { previousByKey };
        },
        onError: (_err, _viewId, context) => {
            if (context?.previousByKey) {
                for (const [json, data] of context.previousByKey) {
                    queryClient.setQueryData(JSON.parse(json), data);
                }
            }
        },
        onSettled: () => {
            invalidateAllViewsForProject(queryClient, projectId);
        },
    });
}

export function useReorderFavoriteViews(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (viewIds: string[]) => {
            return apiPost<ReorderFavoritesResponse>(`/projects/${projectId}/views/favorites/reorder`, { viewIds });
        },
        onMutate: async (viewIds) => {
            const allKeys = queryClient
                .getQueryCache()
                .findAll({ queryKey: viewKeys.lists() })
                .map((q) => q.queryKey)
                .filter((k) => Array.isArray(k) && k[2] === projectId);

            const previousByKey = new Map<string, ViewsResponse | undefined>();
            for (const key of allKeys) {
                previousByKey.set(JSON.stringify(key), queryClient.getQueryData<ViewsResponse>(key));
            }

            for (const key of allKeys) {
                queryClient.setQueryData<ViewsResponse>(key, (old) => {
                    if (!old) return old;
                    const positionMap = new Map(viewIds.map((id, index) => [id, index]));
                    return {
                        ...old,
                        views: old.views.map((view) => {
                            if (positionMap.has(view.id)) {
                                return {
                                    ...view,
                                    favoritePosition: positionMap.get(view.id) ?? view.favoritePosition,
                                };
                            }
                            return view;
                        }),
                    };
                });
            }

            return { previousByKey };
        },
        onError: (_err, _viewIds, context) => {
            if (context?.previousByKey) {
                for (const [json, data] of context.previousByKey) {
                    queryClient.setQueryData(JSON.parse(json), data);
                }
            }
        },
        onSettled: () => {
            invalidateAllViewsForProject(queryClient, projectId);
        },
    });
}
